import { Brand, Embroidery, I18NText, Position, State, Language } from 'src/types'
import { getYellStoryBaseCells, getYellStoryColorCells } from 'src/features/yellStory/simulatedSpecification'
import {
  getDxMBaseCells,
  getDxMCatcherMittColorCells,
  getDxMFirstMittColorCells,
  getDxMGloveColorCells
} from 'src/features/dxm/simulationSpecification'
import {
  getGorillaBaseCells,
  getGorillaCatcherMittColorCells,
  getGorillaFirstMittColorCells,
  getGorillaGloveColorCells
} from 'src/features/gorilla/simulationSpecification'
import {
  getAtomsBaseCells,
  getAtomsCatcherMittColorCells,
  getAtomsFirstMittColorCells,
  getAtomsGloveColorCells
} from 'src/features/atoms/glove/simulationSpecification'
import { en2JaEmbroideryPartsTranslates } from 'src/features/atoms/glove/us/constants/embroidery'
import { ko2JaEmbroideryPartsTranslates } from 'src/features/atoms/glove/ko/constants/embroidery'
import { tw2JaEmbroideryPartsTranslates } from 'src/features/atoms/glove/tw/constants/embroidery'

export const genBaseCells = (state: State, brand: Brand, position: Position, t: I18NText, language: Language) => {
  switch (brand) {
    case 'atoms':
      return getAtomsBaseCells(state, position, t, language)
    case 'yellStory':
      return getYellStoryBaseCells(state)
    case 'dxm':
      return getDxMBaseCells(state, position)
    case 'gorilla':
      return getGorillaBaseCells(state, position)
  }
}

const genGloveColorCells = (state: State, brand: Brand, position: Position, t: I18NText, language: Language) => {
  switch (brand) {
    case 'atoms':
      return getAtomsGloveColorCells(state, position, t, language)
    case 'yellStory':
      return getYellStoryColorCells(state, t)
    case 'dxm':
      // TODO DxMはI18N対応していないので、tを除外し、内部ファイルも修正する
      return getDxMGloveColorCells(state, position, t)
    // DxMと同じ理由でtを除外を検討
    case 'gorilla':
      return getGorillaGloveColorCells(state, t)
  }
}

export const genFirstMittColorCells = (state: State, brand: Brand, t: I18NText, language: Language) => {
  switch (brand) {
    case 'atoms':
      return getAtomsFirstMittColorCells(state, t, language)
    case 'dxm':
      return getDxMFirstMittColorCells(state, t)
    case 'gorilla':
      return getGorillaFirstMittColorCells(state, t)
  }
}

const genCatcherMittColorCells = (state: State, brand: Brand, t: I18NText, language: Language) => {
  switch (brand) {
    case 'atoms':
      return getAtomsCatcherMittColorCells(state, t, language)
    case 'dxm':
      return getDxMCatcherMittColorCells(state, t)
    case 'gorilla':
      return getGorillaCatcherMittColorCells(state, t)
  }
}

export const genColorCells = (
  position: Position,
  state: State,
  brand: Brand,
  t: I18NText,
  language: Language = 'ja'
): {
  head: string
  label: string
  color: string
  value: string
  partsKey: string
}[] => {
  switch (position) {
    case 'catcher':
      return genCatcherMittColorCells(state, brand, t, language)
    case 'first':
      return genFirstMittColorCells(state, brand, t, language)
    default:
      return genGloveColorCells(state, brand, position, t, language)
  }
}

const getLabel = (label: string, language: Language) => {
  if (language !== 'ja') {
    // MEMO: 消費者の言語が外国語の場合は翻訳せずにそのまま表示
    return label
  }
  const translatedFromEn = en2JaEmbroideryPartsTranslates.find((x) => x?.en === label)
  const translatedFromKo = ko2JaEmbroideryPartsTranslates.find((x) => x?.korea === label)
  const translatedFromTaiwan = tw2JaEmbroideryPartsTranslates.find((x) => x?.taiwan === label)
  const translated = translatedFromEn || translatedFromKo || translatedFromTaiwan || { japanese: label }
  return translated.japanese
}

export const genEmbroideryCells = (
  embroidery: Embroidery,
  t: I18NText,
  language: Language
): { head: string; label: string; value?: string; color?: string }[] => {
  return [
    {
      head: t.TYPE_FACE,
      label: getLabel(embroidery.typeFace.label, language),
      value: embroidery.typeFace.value,
      color: embroidery.typeFace.color
    },
    {
      head: t.POSITION,
      label: getLabel(embroidery.position.label, language),
      value: embroidery.position.value,
      color: embroidery.position.color
    },
    {
      head: t.MAIN_COLOR,
      label: getLabel(embroidery.color.label, language),
      value: embroidery.color.value,
      color: embroidery.color.color
    },
    embroidery.typeFace.value === 'arriba' && {
      head: t.POINT_COLOR,
      label: getLabel(embroidery?.pointColor?.label, language),
      value: embroidery?.pointColor?.value,
      color: embroidery?.pointColor?.color
    },
    {
      head: t.SHADOW_COLOR,
      label: getLabel(embroidery.shadowColor.label, language),
      value: embroidery.shadowColor.value,
      color: embroidery.shadowColor.color
    },
    {
      head: t.EDGE_COLOR,
      label: getLabel(embroidery.edgeColor.label, language),
      value: embroidery.edgeColor.value,
      color: embroidery.edgeColor.color
    },
    { head: t.EMBROIDERY_CONTENTS, label: embroidery.content }
  ].filter(Boolean)
}
